import { reportingYear } from "../constants";
import { getGniGroupCode } from "../utilities";
import {
  addSectorTotals,
  calculateMix,
  orderByValues,
  summarize
} from "./data";
import { getChangeData } from "./changeData";
import { getProjectedFundingGapsData } from "../components/themes/theme1/projectedFundingGapsData";
import styleConstants from "../styleConstants";

const calculateGniGroupBySectorChartData = (
  databases,
  indicatorId,
  sectorIds,
  methodIds,
  seriesNames
) => {
  const database = databases.reportingYear;

  const methodSet =
    methodIds ||
    new Set(Object.keys(database.methods).map(id => parseInt(id, 10)));

  let data = addSectorTotals(database.data).filter(
    datum =>
      datum.indicator === indicatorId &&
      sectorIds.has(datum.sector) &&
      methodSet.has(datum.method) &&
      (!datum.year || datum.year === reportingYear)
  );

  const areaIds = new Set([14, 15, 23]);

  console.log(data);

  data = data
    .filter(datum => areaIds.has(datum.area))
    .map(datum => {
      const area = getGniGroupCode(database.areas[datum.area].area);

      let sector = database.sectors[datum.sector];

      sector =
        {
          "Private Sub": "Private Subsidized",
          "Private Non-Sub": "Private Non-Subsidized"
        }[sector] || sector;

      return {
        ...datum,
        area,
        sector
      };
    });

  console.log(data);

  const totalSpend = summarize(data, [])[0].value;

  const areaMix = summarize(data, ["area"])
    .map(datum => ({
      ...datum,
      sector: "Total",
      value: datum.value / totalSpend
    }))
    .reduce((result, datum) => {
      result[datum.area] = datum.value;

      return result;
    }, {});

  const areaTotals = summarize(data, ["area"]).map(datum => ({
    ...datum,
    sector: "Total"
  }));

  const summary = summarize(data, ["area", "sector"]);

  const sectorMix = calculateMix(
    [...summary, ...areaTotals],
    "sector",
    "area",
    "Total"
  ).filter(datum => datum.sector !== "Total");

  return seriesNames.map(seriesName => ({
    name: seriesName.replace("Private ", ""),
    color: styleConstants.categoryColors.sectorColors[seriesName],
    data: orderByValues(
      sectorMix.filter(datum => datum.sector === seriesName),
      ["LIC", "L-MIC", "U-MIC"],
      "area"
    ).map(datum => [datum.area, datum.value * 100, areaMix[datum.area] * 100])
  }));
};

const calculateMethodMixChartData = (databases, indicatorId, sectorId) => {
  const database = databases.reportingYear;

  const areaIds = new Set([14, 15, 23]);

  const sectorIds = new Set(sectorId ? [sectorId] : [3, 6]);

  const data = addSectorTotals(database.data)
    .filter(
      datum =>
        areaIds.has(datum.area) &&
        datum.indicator === indicatorId &&
        sectorIds.has(datum.sector) &&
        datum.year === reportingYear
    )
    .map(datum => ({
      ...datum,
      area: database.areas[datum.area].area,
      method: database.methods[datum.method]
    }));

  return summarize(data, ["area", "method"]);
};

// Table 1.1
export const calculateTheme1CurrentSpendingBySectorTableData = databases => {
  const database = databases.reportingYear;

  const areaIds = new Set([14, 15, 23]);

  const data = database.data
    .filter(datum => datum.indicator === 2 && areaIds.has(datum.area))
    .map(datum => ({
      ...datum,
      area: database.areas[datum.area].area
    }));

  const summary = summarize(addSectorTotals(data), ["area", "sector"]).map(
    datum => ({
      ...datum,
      sector: database.sectors[datum.sector]
    })
  );

  const areaTotals = summarize(data, ["area"]).map(total => ({
    ...total,
    sector: "Total"
  }));

  const sectorTotals = summarize([...areaTotals, ...summary], ["sector"]).map(
    total => ({
      ...total,
      area: "Total"
    })
  );

  return [...summary, ...areaTotals, ...sectorTotals];
};

// Figure 1.2
export const calculateTheme1CurrentSpendingChartData = databases =>
  calculateGniGroupBySectorChartData(
    databases,
    2,
    new Set([1, 2, 4, 5]),
    undefined,
    [
      "Donor",
      "Government",
      "Private Subsidized",
      "Private Non-Subsidized"
    ].reverse()
  );

// Table 1.2
export const calculateTheme1ProjectedFundingGapsBySectorTableData = (
  databases,
  areaId
) => {
  const database = databases.core;

  return getProjectedFundingGapsData(
    database,
    addSectorTotals(database.data),
    new Set([6, 3]),
    areaId
  ).map(datum => ({
    ...datum,
    indicator: `${datum.indicator} (${datum.sector})`
  }));
};

// Table 1.3
export const calculateTheme1ProjectedFundingGapsWithinPublicSectorTableData = (
  databases,
  areaId
) => {
  const database = databases.core;

  return getProjectedFundingGapsData(
    database,
    addSectorTotals(database.data),
    new Set([1, 2]),
    areaId
  ).map(datum => ({
    ...datum,
    indicator: `${datum.indicator} (${datum.sector})`
  }));
};

// Table 1.4
export const calculateTheme1ProjectedFundingGapsByGniGroupTableData = (
  databases,
  sectorId
) => {
  const database = databases.core;

  const areaIds = new Set([14, 15, 23]);
  const indicatorIds = new Set([1, 3]);

  const sectorIds = sectorId
    ? new Set([sectorId])
    : new Set(Object.keys(database.sectors).map(id => parseInt(id, 10)));

  const data = database.data
    .filter(
      datum =>
        areaIds.has(datum.area) &&
        indicatorIds.has(datum.indicator) &&
        sectorIds.has(datum.sector) &&
        (datum.year === 2020 || datum.year === 2025)
    )
    .map(datum => ({
      ...datum,
      area: database.areas[datum.area].area
    }));

  const totals = summarize(data, ["indicator", "year"]).map(datum => ({
    ...datum,
    area: "Total"
  }));

  const summary = summarize([...data, ...totals], ["area", "indicator", "year"])
    .map(datum => ({
      ...datum,
      area: datum.area
    }))
    .map(datum => ({
      ...datum,
      indicator: `${database.indicators[datum.indicator]} (${datum.year})`
    }));

  const getGapsAsProportionOfCostShare = (data, year) =>
    Object.values(
      data
        .filter(datum => datum.year === year)
        .reduce((result, datum) => {
          result[datum.area] = {
            ...(result[datum.area] || {
              area: datum.area
            }),
            [datum.indicator]: datum.value
          };

          return result;
        }, {})
    ).map(datum => ({
      ...datum,
      indicator: `Gap as % of Cost Share (${year})`,
      value: datum[1] / datum[3]
    }));

  return [
    ...summary,
    ...getGapsAsProportionOfCostShare(data, 2020),
    ...getGapsAsProportionOfCostShare(data, 2025),
    ...totals,
    ...getGapsAsProportionOfCostShare(totals, 2020),
    ...getGapsAsProportionOfCostShare(totals, 2025)
  ];
};

// Figure 1.3
export const calculateTheme1CumulativeGapBySectorChartData = databases => {
  const database = databases.core;

  const areaIds = new Set([14, 15, 23]);
  const sectorIds = new Set([1, 2, 3]);

  const data = addSectorTotals(database.data)
    .filter(
      datum =>
        areaIds.has(datum.area) &&
        datum.indicator === 1 &&
        sectorIds.has(datum.sector) &&
        datum.year >= 2021 &&
        datum.year <= 2025
    )
    .map(datum => ({
      ...datum,
      area: database.areas[datum.area].area,
      sector: database.sectors[datum.sector]
    }));

  return summarize(data, ["area", "sector"]);
};

// Figure 1.4
export const calculateTheme1CumulativeGapByGniGroupChartData = databases => {
  const database = databases.allSectorTotals;

  const areaIds = new Set([14, 15, 23]);
  const sectorIds = new Set([1, 2, 3]);

  const data = database.data
    .filter(
      datum =>
        areaIds.has(datum.area) &&
        datum.indicator === 1 &&
        sectorIds.has(datum.sector) &&
        datum.year >= 2021 &&
        datum.year <= 2025
    )
    .map(datum => ({
      ...datum,
      area: database.areas[datum.area].area,
      sector: database.sectors[datum.sector]
    }));

  const summary = summarize(data, ["area", "sector"]);

  const areaTotals = summarize(data, ["area"]).map(datum => ({
    ...datum,
    sector: "Total"
  }));

  return [...areaTotals, ...summary];
};

// Figure 2.1
export const calculateTheme2UsersByMethodChartData = (databases, areaId) => {
  const database = databases.allSectorTotals;

  const sectorIds = new Set([3, 6]);

  const data = database.data
    .filter(
      datum =>
        datum.area === areaId &&
        datum.indicator === 4 &&
        sectorIds.has(datum.sector) &&
        datum.year === reportingYear
    )
    .map(datum => ({
      ...datum,
      area: database.areas[datum.area].area,
      method: database.methods[datum.method]
    }));

  return summarize(data, ["sector", "method"]);
};

// Table 2.1
export const calculateTheme2UsersBySectorTableData = (databases, methodId) => {
  const database = databases.reportingYear;

  const areaIds = new Set([14, 15, 23]);
  const sectorIds = new Set([3, 4, 5, 6]);

  const methodIds = methodId
    ? new Set([methodId])
    : new Set(Object.keys(database.methods).map(id => parseInt(id, 10)));

  const data = addSectorTotals(database.data)
    .filter(
      datum =>
        areaIds.has(datum.area) &&
        datum.indicator === 4 &&
        sectorIds.has(datum.sector) &&
        methodIds.has(datum.method) &&
        datum.year === reportingYear
    )
    .map(datum => ({
      ...datum,
      area: database.areas[datum.area].area,
      sector: database.sectors[datum.sector]
    }));

  const summary = summarize(data, ["area", "sector"]);

  const areaTotals = summarize(
    data.filter(datum => ["Public", "Private"].includes(datum.sector)),
    ["area"]
  ).map(datum => ({
    ...datum,
    sector: "Total"
  }));

  const sectorTotals = summarize([...areaTotals, ...summary], ["sector"]).map(
    datum => ({
      ...datum,
      area: "Total"
    })
  );

  return [...areaTotals, ...summary, ...sectorTotals];
};

// Figure 2.2
export const calculateTheme2ShareOfUsersChartData = (databases, methodId) =>
  calculateGniGroupBySectorChartData(
    databases,
    4,
    new Set([3, 6]),
    methodId && new Set([methodId]),
    ["Private", "Public"]
  );

// Figure 2.3
export const calculateTheme2MethodMixWithinSectorChartData = (
  databases,
  sectorId
) => calculateMethodMixChartData(databases, 4, sectorId);

// Figure 3.2
export const calculateTheme3ShareOfUsersWithinPrivateSectorByMethodChartData = (
  databases,
  areaId
) => {
  const database = databases.allSectorTotals;

  const sectorIds = new Set([4, 5]);

  const data = database.data
    .filter(
      datum =>
        datum.area === areaId &&
        datum.indicator === 4 &&
        sectorIds.has(datum.sector) &&
        datum.method !== 2 &&
        datum.year === reportingYear
    )
    .map(datum => ({
      ...datum,
      sector: database.sectors[datum.sector],
      method: database.methods[datum.method]
    }));

  return summarize(data, ["sector", "method"]);
};

// Figures 3.3 and 3.4
export const calculateTheme3ShareWithinPrivateSectorByGniGroupChartData = (
  databases,
  indicatorId,
  methodId
) => {
  const database = databases.allSectorTotals;

  const areaIds = new Set([14, 15, 23]);
  const sectorIds = new Set([4, 5]);

  const methodIds = methodId
    ? new Set([methodId])
    : new Set(Object.keys(database.methods).map(id => parseInt(id, 10)));

  const data = database.data
    .filter(
      datum =>
        areaIds.has(datum.area) &&
        datum.indicator === indicatorId &&
        sectorIds.has(datum.sector) &&
        methodIds.has(datum.method) &&
        datum.year === reportingYear
    )
    .map(datum => ({
      ...datum,
      area: database.areas[datum.area].area,
      sector: database.sectors[datum.sector]
    }));

  return summarize(data, ["area", "sector"]);
};

// Figures 4.1 and 4.4
export const calculateTheme4MethodMixOfUseVsCostChartData = (
  databases,
  areaId,
  sectorId
) => {
  const database = databases.allSectorTotals;

  const indicatorIds = new Set([4, 6]);

  const sectorIds = new Set(sectorId ? [sectorId] : [3, 6]);

  const data = database.data
    .filter(
      datum =>
        datum.area === areaId &&
        indicatorIds.has(datum.indicator) &&
        sectorIds.has(datum.sector) &&
        datum.year === reportingYear
    )
    .map(datum => ({
      ...datum,
      indicator: database.indicators[datum.indicator],
      method: database.methods[datum.method]
    }));

  return summarize(data, ["indicator", "method"]);
};

// Figure 4.3
export const calculateTheme4ConsumptionCostChartData = (databases, areaId) => {
  const database = databases.allSectorTotals;

  const sectorIds = new Set([3, 6]);

  const data = database.data
    .filter(
      datum =>
        datum.area === areaId &&
        datum.indicator === 6 &&
        sectorIds.has(datum.sector) &&
        datum.year === reportingYear
    )
    .map(datum => ({
      ...datum,
      sector: database.sectors[datum.sector],
      method: database.methods[datum.method]
    }));

  return summarize(data, ["sector", "method"]);
};

// Figure 4.5
export const calculateTheme4ShareOfUsersVsCostChartData = (
  databases,
  methodId
) => {
  const database = databases.reportingYear;

  const areaIds = new Set([14, 15, 23]);
  const indicatorIds = new Set([4, 6]);

  const methodIds = new Set(
    methodId
      ? [methodId]
      : Object.keys(database.methods).map(id => parseInt(id, 10))
  );

  let data = database.data
    .filter(
      datum =>
        areaIds.has(datum.area) &&
        indicatorIds.has(datum.indicator) &&
        methodIds.has(datum.method) &&
        datum.year === reportingYear
    )
    .map(datum => ({
      ...datum,
      indicator: database.indicators[datum.indicator],
      area: database.areas[datum.area].area
    }));

  data = data.map(datum => ({
    ...datum,
    area: getGniGroupCode(datum.area)
  }));

  return summarize(data, ["area", "indicator"]);
};

// Figure 4.6
export const calculateTheme4ShareOfConsumptionCostsByMethodChartData = databases =>
  calculateMethodMixChartData(databases, 6);

// Figure 4.7
export const calculateTheme4ShareOfConsumptionCostsVsUsersBySectorChartData = (
  databases,
  methodId
) => {
  const database = databases.allSectorTotals;

  const areaIds = new Set([14, 15, 23]);
  const indicatorIds = new Set([4, 6]);
  const sectorIds = new Set([3, 6]);

  const methodIds = methodId
    ? new Set([methodId])
    : new Set(Object.keys(database.methods).map(id => parseInt(id, 10)));

  const data = database.data
    .filter(
      datum =>
        areaIds.has(datum.area) &&
        indicatorIds.has(datum.indicator) &&
        sectorIds.has(datum.sector) &&
        methodIds.has(datum.method) &&
        datum.year === reportingYear
    )
    .map(datum => ({
      ...datum,
      area: database.areas[datum.area].area,
      indicator: database.indicators[datum.indicator],
      sector: database.sectors[datum.sector]
    }));

  return summarize(data, ["area", "indicator", "sector"]);
};

// Figures 5.1 and 5.5
export const calculateTheme5ChangeByMethodChartData = (
  databases,
  areaId,
  indicatorId
) => {
  const database = databases.core;

  return getChangeData(
    database.data.filter(
      datum => datum.area === areaId && datum.indicator === indicatorId
    ),
    "method"
  )
    .map(datum => ({
      ...datum,
      method:
        datum.method !== "Total" ? database.methods[datum.method] : "Total"
    }))
    .filter(datum => datum.method !== "Total");
};

// Figure 5.2
export const calculateTheme5ChangeInMethodMixChartData = (
  databases,
  areaId
) => {
  const database = databases.core;

  const data = database.data
    .filter(datum => datum.area === areaId && datum.indicator === 4)
    .map(datum => ({
      ...datum,
      method: database.methods[datum.method]
    }));

  const summary = summarize(data, ["method", "year"]);

  const yearTotals = summarize(data, ["year"]).map(datum => ({
    ...datum,
    method: "Total"
  }));

  const methodMix = calculateMix(
    [...summary, ...yearTotals],
    "method",
    "year",
    "Total"
  );

  const users = summary.reduce((result, datum) => {
    result[`${datum.method} ${datum.year}`] = datum.value;

    return result;
  }, {});

  return getChangeData(methodMix, "method")
    .map(datum => ({
      ...datum,
      users: users[`${datum.method} ${datum.year}`]
    }))
    .filter(datum => !isNaN(datum.year));
};

// Tables 5.1 and 5.2
export const calculateTheme5ChangeByGniGroupTableData = (
  databases,
  indicatorId,
  methodId
) => {
  const database = databases.core;

  const areaIds = new Set([14, 15, 23]);

  const methodIds = methodId
    ? new Set([methodId])
    : new Set(Object.keys(database.methods).map(id => parseInt(id, 10)));

  const data = database.data.filter(
    datum =>
      areaIds.has(datum.area) &&
      datum.indicator === indicatorId &&
      methodIds.has(datum.method)
  );

  return getChangeData(data, "area").map(datum => ({
    ...datum,
    area: datum.area !== "Total" ? database.areas[datum.area].area : "Total"
  }));
};

// Figure 5.3 and 5.6
export const calculateTheme5ChangeByGniGroupChartData = (
  databases,
  indicatorId,
  methodId
) => {
  const database = databases.core;

  const areaIds = new Set([14, 15, 23]);

  const methodIds = new Set(
    methodId
      ? [methodId]
      : Object.keys(database.methods).map(id => parseInt(id, 10))
  );

  return getChangeData(
    database.data.filter(
      datum =>
        areaIds.has(datum.area) &&
        datum.indicator === indicatorId &&
        methodIds.has(datum.method)
    ),
    "area"
  )
    .map(datum => ({
      ...datum,
      area: datum.area !== "Total" ? database.areas[datum.area].area : "Total"
    }))
    .filter(datum => datum.area !== "Total")
    .filter(datum => !isNaN(datum.year));
};

// Figure 5.4
export const calculateTheme5MethodMixChartData = (databases, areaId) => {
  const database = databases.core;

  const years = new Set([2020, 2025, 2030]);

  const data = database.data
    .filter(
      datum =>
        datum.area === areaId && datum.indicator === 4 && years.has(datum.year)
    )
    .map(datum => ({
      ...datum,
      method: database.methods[datum.method]
    }));

  return summarize(data, ["method", "year"]);
};
